import { useEffect, useState, Fragment } from 'react';
import {
  AccordionTitle,
  AccordionContent,
  Accordion,
  Card,
  Icon,
  Label
} from 'semantic-ui-react'
import get from 'lodash/get';
import startsWith from 'lodash/startsWith';

import DeckGL from 'deck.gl'
import { WebMercatorViewport } from 'deck.gl';
import { MVTLayer } from '@deck.gl/geo-layers'
import {FillStyleExtension} from '@deck.gl/extensions';
import { StaticMap } from 'react-map-gl'
import { points } from '@turf/turf';
import { center } from '@turf/center';
import { bbox, bboxPolygon } from '@turf/turf';

import config from 'config';
import { countryLookup } from './country-lookup';

import "./style.scss"

const MAPBOX_API_ACCESS_TOKEN = get(config, 'map.MAPBOX_API_ACCESS_TOKEN')
const MAPBOX_TILESET_360 = get(config, 'map.GADM36_0_TILESET') // Countries
const MAPBOX_TILESET_361 = get(config, 'map.GADM36_1_TILESET') // Subdivisions

const {apiUrl} = config

function ReportMap({ countryCode, isCountryLevel, highRiskRegions, conflictRegions, center, boundingBox }) {
  const [layers, setLayers] = useState([]);
  const [viewState, setViewState] = useState({
    pitch: 0,
    bearing: 0,
  })
  const [mapBoundingbox, setMapBoundingBox] = useState([]);
  const [hasLoaded, toggleHasLoaded] = useState(false);

  useEffect(() => {
    setMapBoundingBox([
      [boundingBox[0], boundingBox[1]],
      [boundingBox[2], boundingBox[3]],
    ])
  }, [boundingBox])

  const onAfterRender = () => {
    if (!hasLoaded) {
      toggleHasLoaded(true);
      const viewport = new WebMercatorViewport({
        height: 300,
        width: 300,
        zoom: 14,
        pitch: 30,
        bearing: 15,
      })
      const {longitude, latitude, zoom} = viewport.fitBounds(mapBoundingbox);
      setViewState((prev) => ({
        ...prev,
        longitude,
        latitude,
        zoom,
      }))
    }
  }

  useEffect(() => {
    if (!viewState.zoom) {
      return;
    }

    setLayers(
      [
      // Add pattern to conflict-regions, only for rendering patterns.
      new MVTLayer({
        id: "regions-conflict",
        data: `https://api.mapbox.com/v4/${MAPBOX_TILESET_361}/{z}/{x}/{y}.vector.pbf?access_token=${MAPBOX_API_ACCESS_TOKEN}`,
        minZoom: 0,
        maxZoom: 10,
        getFillColor: (e) => {
          const code = get(e, 'properties.GID_0')
          if (!isCountryLevel) {
            const name = get(e, 'properties.GID_1')
            if (code === countryCode && conflictRegions.includes(name)) {
              return [0, 0, 0, 100]
            }
          }

          if (isCountryLevel && code === countryCode) {
            if (conflictRegions.length > 0) {
              return [0, 0, 0, 100]
            }
          }

          return [0, 0, 0, 0] // transparent
        },
        getLineColor: (e) => {
          const code = get(e, 'properties.GID_0')
          if (!isCountryLevel && code === countryCode) {
            return [0, 0, 0, 100]
          }
          return [0, 0, 0, 0]
        },
        getLineWidth: 2,
        lineWidthMinPixels: 1,
        fillPatternAtlas: '/assets/images/pattern.png',
        fillPatternMapping: '/assets/pattern.json',
        getFillPattern: f => 'dots',
        getFillPatternScale: (d) => {
          const BASE_ZOOM = 10;
          const zoomDiff = viewState.zoom - BASE_ZOOM;
          // Inverse relationship - zoom in = smaller pattern
          return Math.pow(2, -zoomDiff);
        },
        getFillPatternOffset: [0, 0],
        extensions: [new FillStyleExtension({ pattern: true }

        )]
      }),
      // Highlight CAHRAs (High-risk / conflict-affected)
      new MVTLayer({
        id: "regions-cahra",
        data: `https://api.mapbox.com/v4/${MAPBOX_TILESET_361}/{z}/{x}/{y}.vector.pbf?access_token=${MAPBOX_API_ACCESS_TOKEN}`,
        minZoom: 0,
        maxZoom: 10,
        getFillColor: (e) => {
          const code = get(e, 'properties.GID_0')
          if (!isCountryLevel) {
            const name = get(e, 'properties.GID_1')
            if (code === countryCode && conflictRegions.includes(name)) {
              return [228, 176, 0, 100]
            }

            if ((code === countryCode && highRiskRegions.includes(name))) {
              return [228, 176, 0, 100]
            }
          }

          if (isCountryLevel && code === countryCode) {
            if (highRiskRegions.length > 0) {
              return [228, 176, 0, 100]
            } else {
              return [228, 176, 0, 100]
            }
          }

          return [0, 0, 0, 0] // transparent
        },
        getLineColor: (e) => {
          const code = get(e, 'properties.GID_0')
          if (!isCountryLevel && code === countryCode) {
            return [0, 0, 0, 100]
          }
          return [0, 0, 0, 0]
        },
        getLineWidth: 2,
        lineWidthMinPixels: 1,
      }),
    new MVTLayer({
        id: "country",
        data: `https://api.mapbox.com/v4/${MAPBOX_TILESET_360}/{z}/{x}/{y}.vector.pbf?access_token=${MAPBOX_API_ACCESS_TOKEN}`,
        minZoom: 0,
        maxZoom: 10,
        getFillColor: (e) => {
          const code = get(e, 'properties.GID_0')
          const name = get(e, 'properties.GID_1')
          if (code === countryCode && !conflictRegions.includes(name) && !highRiskRegions.includes(name)) {
            return [228, 176, 0, 100]
          }

          return [0, 0, 0, 0] // transparent
        },
        getLineColor: (e) => {
          const code = get(e, 'properties.GID_0')
          if (countryCode === code) {
            return [0, 0, 0, 100]
          }
          return [0, 0, 0, 0]
        },
        getLineWidth: () => {
          return 2;
        },
        lineWidthUnits: "pixels",
        lineWidthMinPixels: 1,
        lineWidthMaxPixels: 2,
        useDevicePixels: true,
      })
    ]
    )
  }, [countryCode, isCountryLevel, highRiskRegions, conflictRegions, viewState])

  return (
    <div style={{height: "300px", position: "relative"}}>
      <DeckGL
        viewState={viewState}
        controller={false} // Set to true for pan/zoom
        style={{height: "300px"}}
        layers={layers}
        onAfterRender={onAfterRender}
      >
        <StaticMap
          mapboxApiAccessToken={MAPBOX_API_ACCESS_TOKEN}
          style={{height: "100%", width: "100%"}}
      />
      </DeckGL>
    </div>
  )
}

function ReportDownloads({ countryName, recordReports }) {
  return (
    <Card.Group
      itemsPerRow={3}
      style={{marginBottom: "1rem"}}
      className="report-downloads"
    >
      {recordReports.map((report) => {
        const reportId = get(get(report, '_id'), '$oid')
        const isCountryLevel = get(report, 'is_country_level', false)
        const includedRegions = get(report, 'included_regions', [])

        const countryHighRisk = isCountryLevel && includedRegions.every((report) => get(report, 'high_risk', false));

        return (
          <Fragment key={`country-${countryName}-container-${reportId}`}>
            {isCountryLevel && (
              <Card
                className="report-card"
                as="a"
                key={`country-report-${reportId}`}
                href={`${apiUrl}/api/v1/cahras/${reportId}/pdf`}
              >
                <Card.Content>
                  <Card.Description>
                    <div
                      className="report-download-label"
                      style={{
                        display: "flex",
                        gap: "1rem",
                        alignItems: "flex-start",
                        color: "black",
                      }}
                    >
                      <Label style={{background: "rgb(228, 176, 0)"}} size="tiny">
                        {countryHighRisk ? "HR" : "CA"}
                      </Label>
                      {countryName}
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>
            )}
            {!isCountryLevel && (
              <Fragment>
              {includedRegions.map((region) => {
                const regionName = get(region, 'name')
                const isHighRisk = get(region, 'high_risk', false)
                return (
                  <Card
                    className="report-card"
                    key={`report-region-${regionName}`}
                    as="a"
                    href={`${apiUrl}/api/v1/cahras/${reportId}/pdf`}
                  >
                    <Card.Content>
                      <Card.Description>
                        <div
                          className="report-download-label"
                          style={{
                            display: "flex",
                            gap: "1rem",
                            alignItems: "flex-start",
                            color: "black",
                          }}
                        >
                          <Label style={{background: "rgb(228, 176, 0)"}} size="tiny">
                            {isHighRisk ? "HR" : "CA"}
                          </Label>
                          {regionName}
                        </div>
                      </Card.Description>
                    </Card.Content>

                  </Card>
                )
              })}
              </Fragment>
            )}
          </Fragment>
        )
      })}
    </Card.Group>
  )
}


export default function ReportAccordion({ reports }) {
  const [openReports, setOpenReports] = useState([]);

  useEffect(() => {
    const handleHashChange = () => {
      const hashValue = window.location.hash.replace("#", '');
      if (hashValue.trim() === '') {
        return;
      }
      setOpenReports((prev) => [...prev, hashValue])
    };

    handleHashChange(window.location.hash.replace("#", ''))

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const handleAccordionOpen = (reportId) => {
    const updatedOpen = openReports.slice()
    const reportIndex = updatedOpen.findIndex((id) => id === reportId);
    if (reportIndex === -1) {
      updatedOpen.push(reportId);
    } else {
      updatedOpen.splice(reportIndex, 1);
    }
    setOpenReports(updatedOpen);
  }

  return (
    <div
      id="reports-accordion"
    >
      <Accordion>
        {reports.map((record) => {
          const countryCode = get(record, 'country_code')
          const countryName = get(record, 'country_name')
          const recordReports = get(record, 'reports', [])
          const isCountryLevel = recordReports.every((report) => get(report, 'is_country_level', false))
          const includedRegions = recordReports.map((report) => {
            return get(report, 'included_regions');
          }).flat()

          const conflictRegions = includedRegions.filter((report) => {
            return get(report, 'conflict_affected', false)
          }).map((report) => {
            const reportId = get(report, 'auto_id');
            return get(countryLookup, `${reportId}.gadm.gid`)
          });

          const highRiskRegions = includedRegions.filter((report) => {
            return get(report, 'high_risk', false)
          }).map((report) => {
            const reportId = get(report, 'auto_id');
            return get(countryLookup, `${reportId}.gadm.gid`)
          });

          const totalRegions = Object.values(countryLookup).filter((record) => {
            return startsWith(get(record, 'gadm.gid'), countryCode);
          })

          const viewports = recordReports.map((report) => {
            return get(report, 'viewport', []).map((vp) => {
              return [get(vp, 'centroid.lon'), get(vp, 'centroid.lat')]
            })
          }).flat()

          const regionBoxes = recordReports.map((report) => {
            return get(report, 'viewport', []).map((vp) => {
              return bboxPolygon([
                get(vp, 'bounding_box[0]'), get(vp, 'bounding_box[1]'),
                get(vp, 'bounding_box[2]'), get(vp, 'bounding_box[3]'),
              ])
            })
          }).flat()

          const viewportPoints = points(viewports);

          const regionBbox = bbox({
            "type": "FeatureCollection",
            "features": regionBoxes,
          })

          const pointCenter = center(viewportPoints);

          const affectedRegions = isCountryLevel ? totalRegions.length : includedRegions.length

          return (
            <div
              id={`${countryCode}`}
              key={`country-${countryCode}-reports`}
            >
              <AccordionTitle
                active={openReports.includes(countryCode)}
                onClick={() => handleAccordionOpen(countryCode)}
                index={countryCode}
                className="report-accordion-title"
              >
                <div style={{display: "flex"}}>
                  <div style={{minWidth: "95px"}}>
                     <Icon name='dropdown' />
                    ({affectedRegions} / {totalRegions.length})
                  </div>
                  <div>
                    Regions of&nbsp;
                    <span style={{fontWeight: "bold"}}>{countryName}</span>
                    &nbsp;are relevant to scope of Regulation 2017/821
                  </div>
                </div>
              </AccordionTitle>
              <AccordionContent
                active={openReports.includes(countryCode)}
              >
                <div style={{display: "flex", flexDirection: "row", gap: "1rem"}}>
                  {openReports.includes(countryCode) && (
                    <div style={{flex: 1}}>
                      <ReportMap
                        countryCode={countryCode}
                        isCountryLevel={isCountryLevel}
                        conflictRegions={conflictRegions}
                        highRiskRegions={highRiskRegions}
                        center={pointCenter}
                        boundingBox={regionBbox}
                      />
                    </div>
                  )}
                  <div
                    style={{flex: 2}}
                    id={countryCode}
                  >
                    <div>
                      <ReportDownloads
                        countryName={countryName}
                        recordReports={recordReports}
                      />
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </div>
          )
        })}
      </Accordion>
    </div>
  )
}